import { Form, FormField, Input, ModalDialog, Select } from "@rothoblaas/design-system";
import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import TranslatableTextField from "../../../../components/ui/TranslatableTextField";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import type { MaterialCategory as MaterialCategoryCode } from "../../../../http/envelopeDesigner/types/material";
import { CLASSIFICATION_CODE } from "../../../../http/envelopeDesigner/types/materialCategory";
import type {
  MaterialSubcategory,
  MaterialSubcategoryBody,
} from "../../../../http/envelopeDesigner/types/materialSubcategory";
import {
  useAdminEnvelopeDesignerApi,
  useMaterialClassificationApi,
} from "../../../../http/envelopeDesigner/useAdminEnvelopeDesigner";

type Option = { value: string; label: string };

// "custom" is the library's subcategory of the custom materials (material_category_service.go)
const RESERVED_CODE = "custom";

// A subcategory of the material library: its code (set on add only), its category (it moves only with its
// materials: the server refuses otherwise), its name in every language and the admissible-condensate category of
// its materials that have none (none: the category's).
const MaterialSubcategoryFormDialog: FC<{ subcategory?: MaterialSubcategory; onClose: () => void }> = ({
  subcategory,
  onClose,
}) => {
  const i18n = useI18n();
  const t = i18n.translation.envelopeDesignerModule.admin;
  const dispatch = useContext(NotificationDispatch);
  const classificationApi = useMaterialClassificationApi();
  const adminApi = useAdminEnvelopeDesignerApi();
  const categoryOptions: Option[] = (adminApi.useAdminMaterialCategories().data?.items ?? []).map((c) => ({
    value: c.code,
    label: c.name,
  }));
  const condCategoryOptions: Option[] = [{ value: "", label: "—" }].concat(
    (adminApi.useAdminCondCategories().data?.items ?? []).map((c) => ({
      value: c.code,
      label: `${c.name} (${c.rule})`,
    })),
  );

  const defaultValues: MaterialSubcategoryBody = {
    code: subcategory?.code ?? "",
    category: subcategory?.category ?? ("" as MaterialCategoryCode),
    name: subcategory?.name ?? "",
    nameIt: subcategory?.nameIt ?? null,
    nameDe: subcategory?.nameDe ?? null,
    nameFr: subcategory?.nameFr ?? null,
    nameEs: subcategory?.nameEs ?? null,
    condCategory: subcategory?.condCategory ?? null,
  };

  const onSubmit = () => async (formData: MaterialSubcategoryBody) => {
    const body: MaterialSubcategoryBody = {
      ...formData,
      nameIt: formData.nameIt || null,
      nameDe: formData.nameDe || null,
      nameFr: formData.nameFr || null,
      nameEs: formData.nameEs || null,
      condCategory: formData.condCategory || null,
    };
    try {
      if (subcategory) {
        await classificationApi.subcategories.update(subcategory.code, body);
      } else {
        await classificationApi.subcategories.insert(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${subcategory ? i18n.translation.common.edit : i18n.translation.common.add} ${t.subcategory}`}
    >
      <Form<MaterialSubcategoryBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {(form) => (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.code} error={form.formState.errors.code}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={!!subcategory}
                  {...form.register("code", {
                    required: true,
                    pattern: CLASSIFICATION_CODE,
                    validate: (code) => code !== RESERVED_CODE,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="category"
              control={form.control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.category} error={fieldState.error}>
                  {({ labelId }) => (
                    <Select<Option, false>
                      inputId={labelId}
                      value={categoryOptions.find((option) => option.value === field.value) ?? null}
                      options={categoryOptions}
                      placeholder={`${i18n.translation.common.select}...`}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option?.value ?? "")}
                    />
                  )}
                </FormField>
              )}
            />
            <TranslatableTextField form={form} />
            <Controller
              name="condCategory"
              control={form.control}
              render={({ field }) => (
                <FormField label={t.condCategory} isOptional>
                  {({ labelId }) => (
                    <Select<Option, false>
                      inputId={labelId}
                      value={condCategoryOptions.find((option) => option.value === (field.value ?? "")) ?? null}
                      options={condCategoryOptions}
                      onChange={(option) => field.onChange(option?.value || null)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default MaterialSubcategoryFormDialog;
